(function () {
  const CACHE_MAX_AGE = 48 * 60 * 60 * 1000;
  const CACHE_VERSION = 'sidebar-v8';

  const ICON_PLUS =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  const ICON_MINUS =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  /**
   * Sonderzeichen, die NFKD nicht zerlegt.
   */
  const CHAR_MAP = {
    'ł': 'l', 'Ł': 'L',
    'ß': 'ss',
    'ø': 'o', 'Ø': 'O',
    'æ': 'ae', 'Æ': 'AE',
    'đ': 'd', 'Đ': 'D',
    'þ': 'th', 'Þ': 'Th',
  };

  const CHAR_MAP_REGEX = new RegExp(`[${Object.keys(CHAR_MAP).join('')}]`, 'g');

  const handleize = (value) =>
    String(value || '')
      .replace(CHAR_MAP_REGEX, (ch) => CHAR_MAP[ch] || ch)
      .normalize('NFKD')
      .replace(/[\u0300-\u036F]/g, '')
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

  const getLocalePrefix = () => {
    const root = window.Shopify?.routes?.root;
    if (root && root !== '/') {
      return root.replace(/\/+$/, '');
    }
    const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//i);
    return match ? `/${match[1].toLowerCase()}` : '';
  };

  const getLocaleId = () => {
    if (window.Shopify?.locale) return window.Shopify.locale;
    const prefix = getLocalePrefix();
    return prefix ? prefix.replace('/', '') : 'default';
  };

  const groupByLetter = (items) =>
    items.reduce((acc, item) => {
      const itemTitle = item.title || item.name || '';
      const first = itemTitle.charAt(0).toUpperCase() || '#';
      acc[first] = acc[first] || [];
      acc[first].push(item);
      return (
        acc[first].sort((a, b) =>
          (a.title || a.name).localeCompare(b.title || b.name, undefined, { sensitivity: 'base' })
        ),
        acc
      );
    }, {});

  const renderAccordions = (groups, sectionId) =>
    Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map((letter) => {
        const accordionId = `sidebar-${sectionId}-${letter}-body`;
        const itemsHtml = groups[letter]
          .map(
            (item) => `
              <p class="sidebar-ingredients__item">
                <a href="${item.url}">${item.title}</a>
              </p>
            `
          )
          .join('');

        return `
          <details class="accordion" data-collapsible>
            <summary
              class="accordion__title subheading"
              data-collapsible-trigger
              aria-expanded="false"
              aria-controls="${accordionId}"
            >
              <span>${letter}</span>
              ${ICON_PLUS}
              ${ICON_MINUS}
            </summary>
            <div
              id="${accordionId}"
              class="accordion__body rte"
              data-collapsible-body
              role="region"
            >
              <div class="accordion__content" data-collapsible-content>
                ${itemsHtml}
              </div>
            </div>
          </details>
        `;
      })
      .join('');

  /**
   * Merged übersetzte Items (für Anzeige) mit den deutschen
   * Originaldaten (für URL-Generierung). Beide Arrays kommen aus
   * derselben paginierten Abfrage und sind daher index-synchron.
   *
   * items[i].name    = übersetzter Name (PL/FR/DE via Translation Layer)
   * items_de[i].name = deutscher Originalname (immer DE, via | json)
   */
  const mergeItems = (data) => {
    const translated = Array.isArray(data.items) ? data.items : [];
    const german = Array.isArray(data.items_de) ? data.items_de : [];

    if (!german.length) return translated;

    return translated.map((item, index) => {
      const de = german[index];
      return {
        name: item.name || (de?.name ?? ''),
        name_de: de?.name || item.name || '',
      };
    });
  };

  class SidebarInhaltsstoffe extends HTMLElement {
    async connectedCallback() {
      const paginationElement = this.querySelector('[data-pagination]');
      if (!paginationElement?.textContent) return;

      try {
        this.pagination = JSON.parse(paginationElement.textContent);
      } catch (error) {
        console.error('[SidebarInhaltsstoffe] Failed to parse pagination data', error);
        return;
      }

      await this.loadItems();

      if (!this.items?.length) {
        this.closest('section')?.remove();
        return;
      }

      this.classList.remove('is-loading');
      this.buildSidebar();
    }

    async loadItems() {
      const mergedFromPagination = mergeItems(this.pagination);
      const totalPages = Number(this.pagination.pages);

      const totalItems = Number(
        this.pagination.total_items ?? this.pagination.items_count ?? this.pagination.items_total
      );

      const cached = this.readCache();
      if (
        cached &&
        Date.now() - cached.timestamp <= CACHE_MAX_AGE &&
        (Number.isNaN(totalItems) || cached.totalItems === totalItems)
      ) {
        this.items = cached.items;
        return;
      }

      if (!Number.isFinite(totalPages) || totalPages < 1) {
        this.items = mergedFromPagination;
        this.writeCache(this.items, mergedFromPagination.length);
        return;
      }

      const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

      const requests = pageNumbers
        .map((page) => this.buildPageUrl(page))
        .filter(Boolean)
        .map(async (url) => {
          try {
            if (url === window.location.pathname + window.location.search) {
              return mergedFromPagination;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

            const dom = new DOMParser().parseFromString(await response.text(), 'text/html');
            const paginationNode = dom.querySelector('[data-pagination]');
            if (!paginationNode?.textContent) return null;

            const data = JSON.parse(paginationNode.textContent);
            const merged = mergeItems(data);
            return merged.length ? merged : null;
          } catch (error) {
            console.error('[SidebarInhaltsstoffe] Failed to fetch page', url, error);
            return null;
          }
        });

      const results = await Promise.all(requests);

      this.items = results
        .filter((set) => Array.isArray(set))
        .flat();

      if (!this.items.length && mergedFromPagination.length) {
        this.items = mergedFromPagination;
      }

      this.writeCache(this.items, Number.isFinite(totalItems) ? totalItems : this.items.length);
    }

    buildSidebar() {
      const sectionId = this.dataset.sectionId || 'default';
      const localePrefix = getLocalePrefix();

      const entries = this.items
        .map((item) => {
          const displayName = item.name || item.title;
          if (!displayName) return null;

          // URL immer aus dem deutschen Originalnamen generieren,
          // da die Shopify Pages nur auf DE existieren (T-Lab übersetzt nur Content).
          const deName = item.name_de || displayName;

          return {
            title: displayName.trim(),
            url: `${localePrefix}/pages/${handleize(deName)}`,
          };
        })
        .filter(Boolean);

      if (!entries.length) return;

      const groups = groupByLetter(entries);
      const markup = renderAccordions(groups, sectionId);

      if (!markup) return;

      this.innerHTML = `
        <collapsible-elements single="true">
          ${markup}
        </collapsible-elements>
      `;

      this.querySelector('collapsible-elements')
        ?.querySelectorAll('details[data-collapsible]')
        .forEach((detailsEl) => {
          const summary = detailsEl.querySelector('[data-collapsible-trigger]');
          if (!summary) return;

          summary.setAttribute('aria-expanded', detailsEl.hasAttribute('open') ? 'true' : 'false');
          detailsEl.addEventListener('toggle', () => {
            summary.setAttribute('aria-expanded', detailsEl.hasAttribute('open') ? 'true' : 'false');
          });
        });
    }

    buildPageUrl(targetPage) {
      const baseUrl = this.pagination.next_url || window.location.href;

      try {
        const url = new URL(baseUrl, window.location.origin);
        const pageParam = Array.from(url.searchParams.keys()).find((key) => key.startsWith('page')) || 'page';
        url.searchParams.set(pageParam, String(targetPage));
        if (this.dataset.sectionId) {
          url.searchParams.set('section_id', this.dataset.sectionId);
        }
        return `${url.pathname}${url.search}`;
      } catch (error) {
        console.error('[SidebarInhaltsstoffe] Failed to build page URL', error);
        return null;
      }
    }

    get cacheKey() {
      const locale = getLocaleId();
      return `sidebar-inhaltsstoffe:${this.dataset.sectionId || 'default'}:${locale}:${CACHE_VERSION}`;
    }

    readCache() {
      try {
        const serialized = window.localStorage.getItem(this.cacheKey);
        return serialized ? JSON.parse(serialized) : null;
      } catch (error) {
        console.warn('[SidebarInhaltsstoffe] Failed to read cache', error);
        return null;
      }
    }

    writeCache(items, totalItems) {
      try {
        window.localStorage.setItem(
          this.cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            items,
            totalItems,
          }),
        );
      } catch (error) {
        console.warn('[SidebarInhaltsstoffe] Failed to write cache', error);
      }
    }
  }

  if (!customElements.get('sidebar-inhaltsstoffe')) {
    customElements.define('sidebar-inhaltsstoffe', SidebarInhaltsstoffe);
  }
})();